import { useMemo, useReducer, createRef, useState, useEffect } from 'react'
// Contexts
import AppContext from './AppContext'
import AuthContext from './AuthContext'
import GlobalContext from './GlobalContext'
// Reducers
import { initialState, combineReducers } from '../reducers/CombineReducer'
import TreeReducer from '../reducers/TreeReducer'
import ViewReducer from '../reducers/ViewReducer'
import AppReducer from '../reducers/AppReducer'
import IAMReducer from '../reducers/IAMReducer'
import LicenseReducer from '../reducers/LicenseReducer'
import DeviceReducer from '../reducers/DeviceReducer'
import InstanceReducer from '../reducers/InstanceReducer'
import SubscriptionReducer from '../reducers/SubscriptionReducer'
// Utils
import { getAuthState } from '../utils/Sparky'
import { ARRAY } from '../utils/Utils'
// Constants
import { APP_ID_TO_NAME } from '../constants/LabelConstants'
// import { FF_FEDRAMP_HIGH, FF_FEDRAMP_MOD } from '../utils/FeatureFlags'
import { __isDebugOn } from '@sparky/framework'

const reducers = {
  treeReducer: TreeReducer,
  viewReducer: ViewReducer,
  appReducer: AppReducer,
  iamReducer: IAMReducer,
  licenseReducer: LicenseReducer,
  deviceReducer: DeviceReducer,
  instanceReducer: InstanceReducer,
  subscriptionReducer: SubscriptionReducer,
}

// const fedHighEnv = getVarValue(FF_FEDRAMP_HIGH, false)
// const fedModEnv = getVarValue(FF_FEDRAMP_MOD, true)

export const commonDrawerStateRef = createRef({
  initializing: true,
  dispatch: (() => {
    throw new Error('common drawer state store is not initialized yet')
  })
})
const COMMON_DRAWER_STATE_UPDATE = 'state-update'
const event = new EventTarget()
event.listenerCounts = 0
const fireEvent = () => {
  if (event.listenerCounts > 0) {
    event.dispatchEvent(new Event(COMMON_DRAWER_STATE_UPDATE))
  }
}
export const useCommonDrawerState = (commonModule) => {
  const [commonDrawerState, dispatch] = useReducer(combineReducers(reducers), initialState)
  const globalContextValue = useMemo(() => ({
    ...commonDrawerState, commonModule, dispatch
  }), [commonDrawerState, commonModule])
  commonDrawerStateRef.current = globalContextValue
  useEffect(fireEvent, [globalContextValue])
  return globalContextValue
}
export const useLinkedCommonDrawerState = () => {
  const [, setState] = useState()
  useEffect(() => {
    event.addEventListener(COMMON_DRAWER_STATE_UPDATE, setState, { passive: true })
    ++event.listenerCounts
    return () => {
      event.removeEventListener(COMMON_DRAWER_STATE_UPDATE, setState)
      --event.listenerCounts
    }
  }, [])
  return commonDrawerStateRef.current
}

const CommonDrawerContextProviders = ({
  auth = getAuthState(),
  children,
}) => {
  const { appReducer } = commonDrawerStateRef.current
  const appContext = useMemo(() => {
    const apps = appReducer?.apps || getAuthState()?.apps?.reduce((acc, app) => Object.assign(acc, { [app.app_id]: app }), {})
    const appBlacklist = Object.values(apps).reduce((array, app) => {
      if (app.no_app_tile_sase_ui || app.hasOwnProperty('extension_to')) {
        array.push(app.app_id)
      }
      return array
    }, [])
    return {
      apps,
      getAppName: (app_id, defValue) => {
        return apps?.[app_id]?.display_name ?? APP_ID_TO_NAME[defValue] ?? defValue ?? 'None'
      },
      dontRenderApp: (app_id) => appBlacklist.includes(app_id)
    }
  }, [appReducer?.apps])

  const authContext = useMemo(() => {
    const supportAccountIds = ARRAY.clean(auth?.user?.supportAccountIds)
    const user = Object.assign({}, auth?.user, { supportAccountIds })
    return {
      auth: Object.assign({}, auth, { user }),
      user
    }
  }, [auth])

  return (
    <GlobalContext.Provider value={commonDrawerStateRef.current}>
      <AuthContext.Provider value={authContext}>
        <AppContext.Provider value={appContext}>
          {children}
        </AppContext.Provider>
      </AuthContext.Provider>
    </GlobalContext.Provider>
  )
}

if (__isDebugOn()) {
  let commonModule = window.__SPARKY_LIBS__.commonModule
  if (!commonModule) {
    commonModule = {}
    Object.defineProperties(window.__SPARKY_LIBS__.__proto__, {
      commonModule: {
        value: commonModule
      }
    })
  }
  commonModule.commonDrawerStateRef = commonDrawerStateRef
}

export default CommonDrawerContextProviders
